import { useState, useEffect } from 'react';

// components
import Image from 'next/image';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import VisibleLoading from '@/components/utilities/VisibleLoading';

// utilities
import { getImagePath } from '@/utils/images';
import { useAppContext } from '@/components/utilities/AppContext';

const ImagesCarousel = ({ images, random }) => {
  const { locale } = useAppContext();
  const [api, setApi] = useState();
  const [current, setCurrent] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  useEffect(() => {
    if (!api) {
      return;
    }

    const updateScrollSnaps = () => {
      setScrollSnaps(api.scrollSnapList());
    };

    // Initialize state from external carousel API
    // setTimeout defers state updates to avoid cascading renders
    setTimeout(() => {
      updateScrollSnaps();
      setCurrent(api.selectedScrollSnap() + 1);
    }, 0);

    // Subscribe to carousel events (external system updates)
    const handleSelect = () => {
      setCurrent(api.selectedScrollSnap() + 1);
    };

    api.on('select', handleSelect);

    const handleResize = () => {
      setTimeout(updateScrollSnaps, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      api.off('select', handleSelect);
      window.removeEventListener('resize', handleResize);
    };
  }, [api]);

  const onSelectItem = (e) => {
    const index = Number(e.currentTarget.dataset.id) || 0;

    if (api) {
      api.scrollTo(index);
    }
  };

  const onPrev = () => {
    if (api) {
      api.scrollPrev();
    }
  };

  const onNext = () => {
    if (api) {
      api.scrollNext();
    }
  };

  // Client-side only random selection to avoid hydration mismatch
  const [randomIndex, setRandomIndex] = useState(null);

  useEffect(() => {
    if (scrollSnaps.length > 0 && randomIndex === null) {
      setTimeout(() => {
        const randomIndex = Math.floor(Math.random() * scrollSnaps.length);

        setRandomIndex(randomIndex);
      }, 0);
    }
  }, [randomIndex, scrollSnaps]);

  useEffect(() => {
    if (random && randomIndex !== null && api) {
      api.scrollTo(randomIndex);
    }
  }, [random, randomIndex, api]);

  return (
    <VisibleLoading delay={300}>
      {({ ref, className }) => (
        <div ref={ref} className={`my-8 w-full xl:my-16 ${className || ''}`.trim()}>
          <Carousel
            setApi={setApi}
            opts={{
              align: 'center'
            }}
          >
            <CarouselContent className="mr-2 -ml-2 px-6 xl:mr-3 xl:-ml-3">
              {(images || []).map((image, index) => (
                <CarouselItem key={index} className="h-[280px] max-w-[550px] pl-4 xl:h-[450px] xl:px-3">
                  <Image
                    src={getImagePath(image)}
                    width={image.width}
                    height={image.height}
                    alt={image.alternativeText || 'Feature'}
                    sizes="(min-width: 1280px) 800px, 550px"
                    className={`h-full w-full rounded-3xl object-cover`}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          <div className="mt-5 flex flex-wrap justify-center gap-5 lg:mt-10 lg:gap-8">
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                type="button"
                data-id={index}
                aria-label={`${locale !== 'pl' ? 'Zobrazit snímek' : 'Pokaż slajd'} ${index + 1}`}
                aria-current={current === index + 1}
                className="group flex cursor-pointer items-center justify-center bg-transparent p-2"
                onClick={onSelectItem}
              >
                <div
                  className={`h-[8px] w-[8px] cursor-pointer rounded-full lg:h-[10px] lg:w-[10px] ${current === index + 1 ? 'bg-white' : 'bg-grey-500'} `}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </VisibleLoading>
  );
};
export default ImagesCarousel;
